import React from "react";
import Icon from "../../../components/AppIcon.jsx"; // Custom Icon component
import Image from "../../../components/AppImage.jsx"; // Custom Image component
import Button from "../../../components/ui/Button.jsx"; // Custom Button component

// Data for the hero section
const heroData = {
  name: "Rehmat Ullah",
  title: "Front-End-Developer",
  tagline: "Crafting pixel-perfect, high-performance web experiences that delight users.",
  bio: "Passionate about building accessible, responsive interfaces with modern React ecosystems. 3+ years turning complex problems into elegant digital solutions.",
  avatar: "/unnamed.jpg", // Avatar image path
  avatarAlt: "Professional headshot of Rehma Ullah, a young South Asian woman with dark hair wearing a navy blazer against a light background",
  resumeUrl: "/Rehmat Ullah Resume (2).pdf", // Resume download path
  stats: [
    { label: "Projects Completed", value: "40+" }, // Projects counter
    { label: "Month Experience", value: "6+" }, // Experience counter
    { label: "Happy Clients", value: "10+" } // Clients counter
  ],
  socials: [
    { name: "GitHub", icon: "Github", url: "https://github.com/rehmatcodemaster" },
    { name: "Fiverr", icon: "Fiverr", url: "https://www.fiverr.com/users/rehmatullahulla/seller_dashboard" },
    { name: "Instagram", icon: "Instagram", url: "https://www.instagram.com/rehmat_ullah7786/" }
  ]
};

export default function HeroSection() {
  return (
    <div 
      id="home" 
      className="min-h-screen flex items-center pt-[72px] relative overflow-hidden"
      style={{ background: "var(--color-background)" }}
    >
      {/* Background orbs for decorative blur effect */}
      <div
        className="absolute top-1/4 -left-32 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ background: "rgba(124, 106, 247, 0.08)" }}
      />
      <div
        className="absolute bottom-1/4 -right-32 w-80 h-80 rounded-full blur-3xl pointer-events-none"
        style={{ background: "rgba(167, 139, 250, 0.06)" }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none opacity-30"
        style={{ background: "radial-gradient(circle, rgba(124,106,247,0.05) 0%, transparent 70%)" }}
      />

      {/* Main content container */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-20 w-full relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">

          {/* Text Content Section */}
          <div className="flex-1 text-center lg:text-left order-2 lg:order-1">

            {/* Availability Badge */}
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 border"
              style={{
                background: "rgba(124,106,247,0.1)",
                borderColor: "rgba(124,106,247,0.25)",
                color: "var(--color-primary)",
                fontFamily: "var(--font-caption)"
              }}
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
              Available for hire
            </div>

            {/* Name Heading */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4"
              style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}
            >
              Hi, I'm{" "}
              <span style={{ color: "var(--color-primary)" }}>Rehmat Ullah</span>
            </h1>

            {/* Title */}
            <p
              className="text-xl md:text-2xl font-medium mb-4"
              style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-heading)" }}
            >
              {heroData?.title}
            </p>

            {/* Tagline */}
            <p
              className="text-base md:text-lg mb-8 max-w-xl mx-auto lg:mx-0"
              style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-body)" }}
            >
              {heroData?.tagline}
            </p>

            {/* Buttons: Resume Download & Contact */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-10">
              <a
                href="/Rehmat Ullah Resume (2).pdf"
                download="Rehmat Ullah Resume (2).pdf"
              >
                <Button variant="default" size="lg" iconName="Download" iconPosition="left">
                  Download Resume
                </Button>
              </a>

              <Button
                variant="outline"
                size="lg"
                iconName="Mail"
                iconPosition="left"
                onClick={() => {
                  const el = document.getElementById("contact");
                  if (el) el?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Get In Touch
              </Button>
            </div>

            {/* Stats Section */}
            <div className="flex gap-6 justify-center lg:justify-start mb-8">
              {heroData?.stats?.map((stat) => (
                <div key={stat?.label} className="text-center lg:text-left">
                  <div
                    className="text-2xl md:text-3xl font-bold"
                    style={{ fontFamily: "var(--font-heading)", color: "var(--color-primary)" }}
                  >
                    {stat?.value}
                  </div>
                  <div
                    className="text-xs"
                    style={{ color: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }}
                  >
                    {stat?.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="flex gap-3 justify-center lg:justify-start">
              {heroData?.socials?.map((s) => (
                <a
                  key={s?.name}
                  href={s?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s?.name}
                  className="w-10 h-10 flex items-center justify-center rounded-lg border transition-all duration-200 hover:-translate-y-1 hover:shadow-glow"
                  style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
                >
                  <Icon name={s?.icon} size={18} color="var(--color-foreground)" />
                </a>
              ))}
            </div>
          </div>

          {/* Avatar / Image Section */}
          <div className="order-1 lg:order-2 flex-shrink-0">
            <div className="relative">

              {/* Glow ring behind avatar */}
              <div
                className="absolute inset-0 rounded-2xl blur-xl"
                style={{ background: "rgba(124,106,247,0.2)", transform: "scale(1.1)" }}
              />

              {/* Avatar Image Container */}
              <div
                className="relative w-56 h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-2xl overflow-hidden"
                style={{ border: "2px solid rgba(124,106,247,0.4)", boxShadow: "0 0 40px rgba(124,106,247,0.2)" }}
              >
                <Image
                  src={heroData?.avatar}
                  alt={heroData?.avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Corner accent behind avatar */}
              <div
                className="absolute -bottom-3 -right-3 w-full h-full rounded-2xl -z-10"
                style={{ background: "rgba(124,106,247,0.08)", border: "1px solid rgba(124,106,247,0.15)" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}